import { gql, useQuery } from '@apollo/client';
import { VStack } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { getAdminGroupIdFromRouter } from '@/components/admin/groups/GroupDetailPageLayout';
import { usePermissions } from '@/hooks/usePermissions';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';

import {
  Settings_GetGroup,
  Settings_GetGroupVariables,
} from './graphql-types/Settings_GetGroup';

import { BannerImageCard } from './components/BannerImageCard';
import { DetailsCard } from './components/DetailsCard';
import { ProfileImageCard } from './components/ProfileImageCard';
import { SettingsCard } from './components/SettingsCard';

const GET_GROUP = gql`
  query Settings_GetGroup(
    $input: GroupInput!
    $subgroupsInput: SubgroupsInput
  ) {
    group(input: $input) {
      result {
        _id
        ...DetailsCard_GroupFragment
        ...BannerImageCard_GroupFragment
        ...ProfileImageCard_GroupFragment
        ...SettingsCard_GroupFragment
      }
    }
  }
  ${DetailsCard.groupFragment}
  ${BannerImageCard.groupFragment}
  ${ProfileImageCard.groupFragment}
  ${SettingsCard.groupFragment}
`;

export const Settings: React.FC<{ groupId?: string }> = ({
  groupId: groupIdProp,
}) => {
  const router = useRouter();
  const groupId = groupIdProp ?? getAdminGroupIdFromRouter(router);

  const permissions = usePermissions();

  const { data, loading } = useQuery<
    Settings_GetGroup,
    Settings_GetGroupVariables
  >(GET_GROUP, {
    skip: permissions.isLoading || !groupId,
    variables: {
      input: {
        _id: groupId ?? '',
      },
      subgroupsInput: {
        filter: permissions.canDoAdminStuffAndAuthenticated
          ? { includeAll: true }
          : undefined,
      },
    },
  });

  const group = data?.group.result;

  return !groupId || loading || permissions.isLoading ? (
    <Loading />
  ) : !group ? (
    <UnexpectedError />
  ) : (
    <VStack spacing="6" w="full">
      <DetailsCard group={group} />
      <BannerImageCard group={group} />
      <ProfileImageCard group={group} />
      <SettingsCard
        group={{
          ...group,
          isGlobalSingleton: group.isGlobalSingleton ?? false,
        }}
      />
    </VStack>
  );
};
